import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { FormProvider, useForm } from 'react-hook-form';
import { Box } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { EditSquareIcon } from '@components';
import { ModalV2 } from 'components/common/modals';
import { IconButtonV2, ButtonV2 } from 'components/common/ui';
import { useToggleModal, useDebounce } from '@hooks';
import { FieldV2 } from 'components/common/form-controls';

import { availabilityServices } from '@services';
import { FILTER_RULE_ENUMS } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';

import validationSchema from './validationSchema';

const styles = {
  row: {
    display: 'flex',
    gap: '20px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
};

const PAGE = 1;
const PAGE_SIZE = 50;
const DEBOUNCE_MS = 300;

const toOption = (item) => ({
  value: item.vehicleInternalId,
  text: [item.vehicleInternalId, item.plate, item.brand, item.model, item.skuId]
    .filter(Boolean)
    .join(' • '),
});

const passThroughFilter = (opts) => opts;

const EditCarId = ({
  vehicleId,
  loading,
  onChange,
  open: controlledOpen,
  onClose: onControlledClose,
  hideTrigger = false,
  modalId = 'edit-car-id-modal',
  formId = 'edit-car-id-form',
}) => {
  const resolver = yupResolver(validationSchema);
  const [savedOption, setSavedOption] = useState(null);
  const initialOption = useMemo(() => {
    if (!vehicleId) return null;
    if (savedOption && savedOption.value === vehicleId) return savedOption;
    return { value: vehicleId, text: String(vehicleId) };
  }, [vehicleId, savedOption]);
  const methods = useForm({
    defaultValues: { vehicleId: initialOption },
    resolver,
  });

  const { isOpen, handleClose, handleOpen } = useToggleModal();
  const isModalOpen = controlledOpen !== undefined ? controlledOpen : isOpen;

  const [inputValue, setInputValue] = useState('');
  const debouncedQuery = useDebounce(inputValue, DEBOUNCE_MS);

  const { data: searchResults = [], isFetching } = useQuery(
    [QUERY_KEYS.GET_AVAILABILITY_KEY, 'edit-car-id', debouncedQuery],
    async () => {
      const params = { page: PAGE, pageSize: PAGE_SIZE };
      if (debouncedQuery) {
        params.query = debouncedQuery;
      }
      const res = await availabilityServices.getAvailability({
        params,
        payload: { type: FILTER_RULE_ENUMS.EmptyRuleType },
      });
      return res?.data || [];
    },
    {
      enabled: isModalOpen,
      keepPreviousData: true,
    }
  );

  const options = useMemo(() => {
    const mapped = searchResults
      .filter((item) => item?.vehicleInternalId != null)
      .map(toOption);
    if (
      initialOption &&
      !mapped.some((opt) => opt.value === initialOption.value)
    ) {
      return [initialOption, ...mapped];
    }
    return mapped;
  }, [searchResults, initialOption]);

  const handleModalClose = () => {
    setInputValue('');
    methods.reset({ vehicleId: initialOption });
    onControlledClose?.();
    if (controlledOpen === undefined) {
      handleClose();
    }
  };

  const handleInputChange = useCallback((newInputValue, reason) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
    } else if (reason === 'clear') {
      setInputValue('');
    }
  }, []);

  const handleSubmit = async (values) => {
    setSavedOption(values.vehicleId || null);
    await onChange({
      field: 'vehicleId',
      value: values.vehicleId?.value,
    });
    if (controlledOpen === undefined) {
      handleClose();
    }
  };

  return (
    <>
      {!hideTrigger && (
        <IconButtonV2 variant="text" onClick={handleOpen}>
          <EditSquareIcon color="#3B82F6" height={16} />
        </IconButtonV2>
      )}

      <ModalV2
        id={modalId}
        title="Car ID"
        isOpen={isModalOpen}
        isLoading={loading}
        onClose={handleModalClose}
        size="sm"
        maxWidth="sm"
      >
        <FormProvider {...methods}>
          <form id={formId} onSubmit={methods.handleSubmit(handleSubmit)}>
            <Box sx={styles.row}>
              <FieldV2
                isRequired
                label="Enter the Car ID"
                type="autocomplete"
                control={methods.control}
                name="vehicleId"
                options={options}
                loading={isFetching}
                placeholder="Search Car ID"
                onInputChange={handleInputChange}
                filterOptions={passThroughFilter}
              />
            </Box>

            <Box sx={styles.footer}>
              <ButtonV2
                type="button"
                variant="outlined"
                onClick={handleModalClose}
              >
                Cancel
              </ButtonV2>
              <ButtonV2
                form={formId}
                type="submit"
                disabled={loading || methods.formState.isSubmitting}
              >
                Save
              </ButtonV2>
            </Box>
          </form>
        </FormProvider>
      </ModalV2>
    </>
  );
};

export default EditCarId;
